// BUILD-046 task 8.1: typed client for the Shared Tasks / My Work routes (design "Route table", "Frontend components").
//
// Why a second client: api/client.ts throws `new Error(message)` (the HTTP status is lost) and answers a 401 with
// `window.location.reload()`, which loops forever when the identity cannot be resolved. This client
//  - preserves `status`, `code` and `details` on every failure (WorkApiError);
//  - NEVER reloads the page and never retries on 401 (Req 2.4: an authentication failure is shown, not looped);
//  - tags every Caller-scoped request with the Caller epoch and DROPS an answer whose request began under an older epoch
//    (rejects with kind 'stale'), so a list or count obtained for the previous Caller can never be shown (Req 2.7, 2.12);
//  - dispatches `caller-changed` on `window` when the Caller changes, whether the switcher says so (callerSwitched) or the
//    server's `X-Caller-Employee-Id` response header changes (the pilot persona is held by the server, so another tab can switch it);
//  - bounds every request with an AbortController: 30 s for lists, 10 s for Done, Undo and counts (Req 8.3, 15.4);
//  - refuses an answer that is not the documented shape (a 200 without `items` is a failure, never an empty list: Req 15.7).
//
// The request carries exactly two headers, Content-Type and Authorization, because the pilot's CORS preflight allows no others.
// Erasable TypeScript only (no enums, namespaces or parameter properties): the unit tests load this file with
// `node --experimental-strip-types`. Requirements: 2.4, 2.7, 2.12, 15.4.

import { config } from '../config';
import { getAuthToken } from './client';

// ───────────────────────── constants ─────────────────────────

export const CALLER_CHANGED_EVENT = 'caller-changed';
export const TASKS_CHANGED_EVENT = 'shared-tasks:changed';
export const CALLER_HEADER = 'X-Caller-Employee-Id';

export type TimeoutClass = 'list' | 'done' | 'count' | 'action';

/** Req 15.4 (30 s lists) and Req 8.3 / design "Done" (10 s Done, Undo and counts). */
export const TIMEOUTS_MS: Readonly<Record<TimeoutClass, number>> = Object.freeze({
  list: 30000,
  done: 10000,
  count: 10000,
  action: 30000,
});

// ───────────────────────── errors ─────────────────────────

/**
 * `http`: the server answered non-2xx (status, code and details are the server's). `network`: no answer (status 0).
 * `timeout`: no answer within the class limit (status 0). `stale`: the answer belonged to an older Caller epoch and was dropped
 * (status 0; never shown). `invalid-response`: a 2xx answer that is not the documented shape.
 */
export type WorkApiErrorKind = 'http' | 'network' | 'timeout' | 'stale' | 'invalid-response';

export interface WorkApiErrorInit {
  status?: number;
  code?: string | null;
  details?: Record<string, unknown> | null;
  callerEmployeeId?: string | null;
}

export class WorkApiError extends Error {
  kind: WorkApiErrorKind;
  /** HTTP status, or 0 when there was no usable answer. */
  status: number;
  /** The server's `error` code (for example ALREADY_CLAIMED), or null. */
  code: string | null;
  /** The server's `details` object (for example `{ owner: { employeeId, displayName } }` on ALREADY_CLAIMED), or null. */
  details: Record<string, unknown> | null;
  /** The `X-Caller-Employee-Id` of the answer, when it carried one. */
  callerEmployeeId: string | null;

  constructor(kind: WorkApiErrorKind, message: string, init: WorkApiErrorInit = {}) {
    super(message);
    this.name = 'WorkApiError';
    this.kind = kind;
    this.status = init.status ?? 0;
    this.code = init.code ?? null;
    this.details = init.details ?? null;
    this.callerEmployeeId = init.callerEmployeeId ?? null;
  }
}

export function isWorkApiError(value: unknown): value is WorkApiError {
  return value instanceof WorkApiError;
}

/** True for an answer that was dropped because the Caller changed. A view ignores it: it is neither data nor a failure. */
export function isStaleResponse(value: unknown): boolean {
  return value instanceof WorkApiError && value.kind === 'stale';
}

// ───────────────────────── response types (design "Key request and response shapes") ─────────────────────────

export type WorkStatusName = 'active' | 'in_progress' | 'waiting' | 'blocked' | 'completed';
export type WorkAssignmentKind = 'direct' | 'shared' | 'everyone';
export type AssignModeName = 'anyone' | 'everyone';

export interface WorkEmployeeRef {
  employeeId: string;
  displayName: string;
  department?: string;
}

/** `GET /work/me`. Capability flags are advice for the UI; the server decides every action (Req 14.1). */
export interface WorkMe {
  employee: WorkEmployeeRef;
  canOversee: boolean;
  companyOversight: boolean;
  /**
   * May the Caller assign tasks (the `create` rule `POST /work/assign` enforces: every active Employee). It is NOT `canOversee` (that is the
   * Team view) and NOT `canAdministerGroups`. A server that does not state it is read as false, so the UI offers less, never more.
   */
  canAssign: boolean;
  /**
   * May the Caller search the limited job directory (BUILD-046 J1): the assign right AND a wired directory. A server that does not state it is
   * read as false, so the job field, the Compact column and the filter stay hidden against a server that predates jobs.
   */
  canSearchJobs: boolean;
  /**
   * May the Caller search the limited customer directory (BUILD-046 customer association): the assign right AND a wired directory. A server that does
   * not state it is read as false, so the customer field, the Compact column and the filter stay hidden against a server that predates customers.
   */
  canSearchCustomers: boolean;
  /**
   * Does the platform say the Caller has actual Pricing app access (BUILD-046 customer association). ADVICE FOR THE UI ONLY: it decides whether the
   * "Not in the list? Add it in Pricing" note is a link or plain text. It grants nothing; Pricing enforces its own access on click. A server that does
   * not state it is read as false, so the UI offers the plain note, never the link.
   */
  canAddCustomerInPricing: boolean;
  /**
   * What the server could tell about the Caller's Pricing access (BUILD-046 customer association), so a check that could not be made is not shown as "no access":
   * `allowed` | `not-allowed` | `unavailable` (the server could not verify it) | `not-offered` (no customer picker, or no check wired). ADVICE FOR THE UI ONLY, like
   * `canAddCustomerInPricing`. Anything this client does not recognise (including a server that does not state it) is read as `unavailable` while the customer picker is offered
   * and `not-offered` otherwise: it can never become `allowed`.
   */
  pricingCustomerAccess: PricingCustomerAccess;
  canAdministerGroups: boolean;
  simulated: boolean;
  identity?: { simulated: boolean; label: string };
}

/** `GET /work/me` `pricingCustomerAccess`. */
export type PricingCustomerAccess = 'allowed' | 'not-allowed' | 'unavailable' | 'not-offered';

/** The server's `pricingCustomerAccess`, read fail-closed: only the four known words count; anything else is `unavailable` (picker offered) or `not-offered` (picker not offered), never `allowed`. */
export function readPricingCustomerAccess(value: unknown, canSearchCustomers: boolean): PricingCustomerAccess {
  if (value === 'allowed' || value === 'not-allowed' || value === 'unavailable' || value === 'not-offered') return value;
  return canSearchCustomers ? 'unavailable' : 'not-offered';
}

/** One job in the picker (`GET /work/jobs`). Only these fields leave the server; `customer` and `code` help tell two similar names apart. */
export interface WorkJobPick {
  jobId: string;
  label: string;
  customer?: string;
  code?: string;
}
/** One customer in the picker (`GET /work/customers`). Only these fields leave the server; `place` helps tell two similar names apart. */
export interface WorkCustomerPick {
  customerId: string;
  label: string;
  /** City and state, e.g. "Mesa, AZ"; absent when the directory knows neither. */
  place?: string;
}

/** `GET /work/jobs?q=`. `more` is true when the server held back results beyond the cap; fewer than `minChars` characters returns nothing. */
export interface WorkJobSearch {
  jobs: WorkJobPick[];
  more: boolean;
  minChars: number;
}
/** `GET /work/customers?q=`. `more` is true when the server held back results beyond the cap; fewer than `minChars` characters returns nothing. */
export interface WorkCustomerSearch {
  customers: WorkCustomerPick[];
  more: boolean;
  minChars: number;
}

/**
 * The job a task or request carries, as the server shows it. `label` is the CURRENT name when the directory answered, otherwise the stored
 * name (`lastKnown`); `missing` means the directory answered and no longer knows the job. A viewer who may see the task may see this label;
 * it grants no access to the job itself.
 */
export interface WorkJobView {
  jobId: string;
  label: string;
  state?: 'active' | 'archived';
  lastKnown?: true;
  missing?: true;
}
/**
 * The customer a task or request carries, as the server shows it. `label` is the CURRENT name when the directory answered, otherwise the stored
 * name (`lastKnown`); `missing` means the directory answered and no longer knows the customer. A viewer who may see the task may see this label;
 * it grants no access to the customer itself. `place` ("Mesa, AZ") is carried by `GET /work/customers/{id}` only.
 */
export interface WorkCustomerView {
  customerId: string;
  label: string;
  place?: string;
  state?: 'active' | 'inactive';
  lastKnown?: true;
  missing?: true;
}

export interface WorkOwnerLeft {
  groupId: string;
  groupName: string;
  /** `Owner left <Group> on <date>` or `Owner left <Group> (date unavailable)`: the server's text, never recomputed. */
  text: string;
  removedOn: string | null;
}

export interface WorkSourceContext {
  available: boolean;
  label?: string | null;
  customer?: string | null;
  project?: string | null;
  url?: string | null;
  simulated?: boolean;
}

export interface WorkEvidenceView {
  kind: 'file' | 'record';
  available: boolean;
  name?: string;
  url?: string;
  simulated?: boolean;
}

/**
 * A task as the routes return it: the stored record, projected for the viewer (a task whose restricted source the viewer cannot open
 * carries `redacted: true`, the static title "Restricted task" and only identity and state fields), plus read-time fields.
 */
export interface WorkTask {
  id: string;
  title: string;
  description?: string;
  status: WorkStatusName;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assignmentKind?: WorkAssignmentKind;
  ownerId?: string;
  ownerEmployeeId?: string;
  ownerName?: string;
  department?: string;
  requestId?: string;
  requesterEmployeeId?: string;
  requesterName?: string;
  recipientSummary?: { employeeId: string; name: string }[];
  groupRefs?: { groupId: string; name: string }[];
  claim?: { by: string; byName: string; at: string };
  sourceRef?: { module: string; recordId: string };
  sourceGoverned?: boolean;
  /** The Customer / Job the task is about (BUILD-046 J1), separate from `sourceRef`. Absent when none, or when the viewer sees a redacted row. */
  job?: WorkJobView;
  /** The Customer the task is about (BUILD-046 J1), separate from `sourceRef`. Absent when none, or when the viewer sees a redacted row. */
  customer?: WorkCustomerView;
  requiresEvidence?: boolean;
  evidenceRefs?: { module: string; recordId: string; kind: 'file' | 'record' }[];
  collaborators?: string[];
  nextAction?: string;
  followUpDate?: string;
  dueDate?: string;
  notes?: string;
  /** The latest progress note (a short copy), when the server recorded it and the day the work happened (BUILD-046 section C). Withheld on a redacted row. */
  lastProgressText?: string;
  lastProgressAt?: string;
  lastActivityDate?: string;
  completedAt?: string;
  completedBy?: string;
  completedByName?: string;
  lifecycle?: 'cancelled';
  outcome?: 'removed' | 'cancelled';
  createdAt: string;
  updatedAt: string;
  // read-time fields
  unclaimed?: boolean;
  recipientLabel?: string;
  overdue?: boolean;
  reassignmentNeeded?: boolean;
  redacted?: boolean;
  ownerLeft?: WorkOwnerLeft[];
  sourceContext?: WorkSourceContext;
  evidence?: WorkEvidenceView[];
}

export interface WorkList {
  items: WorkTask[];
}

export interface WorkOversightList extends WorkList {
  scope?: 'company' | 'manager';
}

/** Paging for the lists that page (`limit` and the opaque `cursor` the previous page returned as `next`). */
export interface PageQuery {
  limit?: number;
  cursor?: string | null;
}

/**
 * `GET /work/oversight?status=completed` (Builder decision 1bb88b3e, F1): the COMPLETED tasks in the Caller's oversight scope, newest first.
 * `filter` is echoed by the server; an answer without it came from a server that ignored `status` and is refused (see `listOversightCompleted`).
 */
export interface WorkOversightCompletedPage extends WorkOversightList {
  filter: 'completed';
  /** The cursor of the next page; absent on the last page. */
  next?: string;
}

/** One request the Caller issued (`GET /work/issued`). An Everyone request carries `set`, any other carries the task as `item`. */
export interface WorkIssuedEntry {
  requestId: string;
  mode: 'everyone' | 'anyone' | 'direct';
  createdAt: string;
  title: string;
  groupNames?: string[];
  /** The set view of `GET /work/sets/{requestId}` (its `set` member, not wrapped): figure, requester, requestState and the readable members. */
  set?: Record<string, unknown>;
  item?: WorkTask;
  /** The Customer / Job of an Everyone request (BUILD-046 J1a); any other request carries it on `item`. */
  job?: WorkJobView;
  /** The Customer of an Everyone request (BUILD-046 J1a); any other request carries it on `item`. */
  customer?: WorkCustomerView;
}

export interface WorkIssuedPage {
  requests: WorkIssuedEntry[];
  next?: string;
}

/** The optional note of Done with a note. `activityDate` is `YYYY-MM-DD`; the server defaults it to today in Phoenix and refuses a future date. */
export interface WorkNoteInput {
  text: string;
  activityDate?: string;
}

/** The activity entry the server recorded (an attributed, dated Progress entry written in the same step as the completion). */
export interface WorkCompletionNote {
  type: 'progress';
  id: string;
  timestamp: string;
  actor: { employeeId: string; name: string };
  activityDate: string;
  recordedPhoenixDate: string;
  viaCompletion: true;
  text: string;
}

export interface WorkBellEntry {
  id: string;
  title: string;
  status: WorkStatusName;
  dueDate: string | null;
  overdue: boolean;
  waiting: boolean;
}

/** `GET /work/bell`: the labelled counts. A number that cannot be read is a failure, never zero. */
export interface WorkBell {
  openTaskCount: number;
  sharedQueueCount: number;
  entries: WorkBellEntry[];
}

export interface WorkEmployeeList {
  employees: WorkEmployeeRef[];
}

export interface WorkGroupRef {
  groupId: string;
  name: string;
}

export interface WorkGroupMember {
  employeeId: string;
  displayName: string;
  status: 'active' | 'inactive';
}

export interface AssignRecipients {
  employeeIds: string[];
  groupIds: string[];
}

/** `POST /work/assign` and `POST /work/assign/preview` (the preview needs no title). */
export interface AssignRequest {
  submissionId?: string;
  title?: string;
  details?: string | null;
  recipients: AssignRecipients;
  mode?: AssignModeName | null;
  dueDate?: string | null;
  collaborators?: string[];
  nextAction?: string | null;
  sourceRef?: { module: string; recordId: string } | null;
  requiresEvidence?: boolean;
  /** `POST /work/assign` only (the preview ignores it): exactly low, medium, high or urgent; omitted or null means medium. Status is not accepted at assignment. */
  priority?: 'low' | 'medium' | 'high' | 'urgent' | null;
  /** `POST /work/assign` only (the preview ignores it): the stable job id from the picker. The server checks it and stores the name itself; absent or null means no job. */
  jobId?: string | null;
  /** `POST /work/assign` only (the preview ignores it): the stable customer id from the picker. The server checks it and stores the name itself; absent or null means no customer. */
  customerId?: string | null;
}

export interface AssignExcluded {
  employeeId: string;
  name: string;
  reasons: string[];
}

export interface AssignPreview {
  eligibleCount: number;
  involvesGroup: boolean;
  modeRequired: boolean;
  mode: 'direct' | AssignModeName | null;
  recipients: { employeeId: string; name: string }[];
  groups: { groupId: string; name: string; eligibleMemberCount: number }[];
  excluded: AssignExcluded[];
  queueEmptyAtIssue: boolean;
}

export interface AssignResponse {
  requestId: string;
  mode: 'direct' | AssignModeName;
  replayed: boolean;
  items: { id: string; ownerEmployeeId: string | null }[];
  excluded: AssignExcluded[];
  aggregate?: { n: number; m: number; label: string };
  queueEmptyAtIssue?: boolean;
}

export interface WorkItemResponse {
  item: WorkTask;
  alreadyCompleted?: boolean;
  restoredFrom?: string;
  restoredTo?: string;
  recovery?: boolean;
}

/**
 * `POST /work/{id}/complete`. Plain Done answers `{ item }` only. With a note: `noteSaved: true` plus the recorded `note` (also on an exact retry,
 * `noteReplayed`), or `alreadyCompleted: true` with `noteSaved: false` and a `noteReason` (nothing was written). RULE FOR THE UI: when a note was
 * sent, anything but `noteSaved === true` means the note is NOT saved, whatever else the answer says.
 */
export interface WorkCompleteResponse extends WorkItemResponse {
  noteSaved?: boolean;
  noteReason?: string;
  noteReplayed?: boolean;
  note?: WorkCompletionNote;
}

/**
 * The Update form's body (`POST /work/{id}/progress`, BUILD-046 section C). Only what changed is sent; `null` clears a date or the next action. The server decides every rule
 * (a date change needs `changeReason`; Waiting and Blocked need a next action and a follow-up date); the form shows the server's words and keeps what was typed.
 */
export interface WorkProgressInput {
  status?: Exclude<WorkStatusName, 'completed'>;
  note?: WorkNoteInput;
  nextAction?: string | null;
  followUpDate?: string | null;
  dueDate?: string | null;
  changeReason?: string;
}

/** The activity entry an Update recorded (the same shape as a history entry of type progress). */
export type WorkProgressNote = Omit<WorkCompletionNote, 'viaCompletion'>;

/** `POST /work/{id}/progress`: the updated task, the fields that changed (and `status` when it moved) and whether a note was saved. */
export interface WorkProgressResponse extends WorkItemResponse {
  noteSaved: boolean;
  changed: string[];
  note?: WorkProgressNote;
}

/** Code carried by the `invalid-response` error of a completed-task list that came from a server which does not serve the filter. */
export const FILTER_NOT_APPLIED_CODE = 'FILTER_NOT_APPLIED';

/**
 * True when the server does not serve a NEW route: it answered 404, 405 or 501 for it, or it ignored the completed filter. A view shows "not
 * available on this server" for that, never an empty list, and never the generic failure text.
 */
export function isRouteUnavailable(value: unknown): boolean {
  if (!(value instanceof WorkApiError)) return false;
  if (value.code === FILTER_NOT_APPLIED_CODE) return true;
  return value.kind === 'http' && (value.status === 404 || value.status === 405 || value.status === 501);
}

export interface PilotPersona {
  employeeId: string;
  key: string;
  name: string;
  isFixture: boolean;
  simulated: boolean;
}

/** `GET` and `POST /local/switch-caller` (the BUILD-043 contract plus the pilot's additions). */
export interface PilotCallerList {
  currentCallerEmployeeId: string | null;
  currentCallerKey: string | null;
  people: PilotPersona[];
  simulated: boolean;
  label: string;
}

export interface PilotResetResult {
  ok: boolean;
  simulated: boolean;
  archived: string | null;
  seededAt: string | null;
  counts: Record<string, number>;
  currentCallerKey: string | null;
}

// ───────────────────────── client ─────────────────────────

export interface WorkClientDeps {
  /** The API base URL, read on every request (VITE_API_URL through config.ts). */
  baseUrl: () => string;
  getToken: () => Promise<string>;
  fetchImpl: (url: string, init: RequestInit) => Promise<Response>;
  setTimeoutFn: (fn: () => void, ms: number) => unknown;
  clearTimeoutFn: (handle: unknown) => void;
  /** Delivers a named event with a detail object (default: a CustomEvent on `window`, when there is one). */
  dispatch: (name: string, detail: unknown) => void;
  /** Overrides for the limits above (tests use small values); a missing class keeps the default. */
  timeouts: Partial<Record<TimeoutClass, number>>;
}

function defaultDispatch(name: string, detail: unknown): void {
  const w = (globalThis as any).window;
  if (w && typeof w.dispatchEvent === 'function' && typeof CustomEvent === 'function') w.dispatchEvent(new CustomEvent(name, { detail }));
}

function defaultDeps(): WorkClientDeps {
  return {
    baseUrl: () => String(config.apiUrl || '').replace(/\/+$/, ''),
    getToken: () => getAuthToken(),
    fetchImpl: (url, init) => (globalThis as any).fetch(url, init),
    setTimeoutFn: (fn, ms) => setTimeout(fn, ms),
    clearTimeoutFn: (handle) => clearTimeout(handle as any),
    dispatch: defaultDispatch,
    timeouts: TIMEOUTS_MS,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function invalid(what: string, status: number): WorkApiError {
  return new WorkApiError('invalid-response', `The server's answer for ${what} was not in the expected form.`, { status });
}

function enc(value: string): string {
  return encodeURIComponent(value);
}

/** `&limit=20&cursor=...` (or with `?` for the first parameter); '' when the query names neither. A limit that is not a positive integer is left out. */
function pageParams(query: PageQuery, lead: '&' | '?' = '&'): string {
  const parts: string[] = [];
  if (typeof query.limit === 'number' && Number.isInteger(query.limit) && query.limit > 0) parts.push(`limit=${query.limit}`);
  if (typeof query.cursor === 'string' && query.cursor !== '') parts.push(`cursor=${enc(query.cursor)}`);
  return parts.length === 0 ? '' : `${lead}${parts.join('&')}`;
}

/** The `next` cursor of a page: absent on the last page; present but not a non-empty string is a failure (never "no more"). */
function nextCursor(body: Record<string, unknown>, what: string): string | undefined {
  if (body.next === undefined || body.next === null) return undefined;
  if (typeof body.next !== 'string' || body.next === '') throw invalid(what, 200);
  return body.next;
}

function isIssuedEntry(value: unknown): boolean {
  if (!isRecord(value) || typeof value.requestId !== 'string' || value.requestId === '') return false;
  if (value.mode !== 'everyone' && value.mode !== 'anyone' && value.mode !== 'direct') return false;
  if (typeof value.title !== 'string') return false;
  if (value.set !== undefined && !isRecord(value.set)) return false;
  if (value.item !== undefined && !(isRecord(value.item) && typeof value.item.id === 'string')) return false;
  return true;
}

interface RequestSpec {
  method?: string;
  body?: unknown;
  timeout: TimeoutClass;
  /** False for the pilot's `/local/*` routes: they are not scoped to a Caller, so they are never dropped as stale. */
  scoped?: boolean;
}

export function createWorkClient(overrides: Partial<WorkClientDeps> = {}) {
  const deps: WorkClientDeps = { ...defaultDeps(), ...overrides };
  const timeouts: Readonly<Record<TimeoutClass, number>> = { ...TIMEOUTS_MS, ...(overrides.timeouts ?? {}) };

  let epoch = 0;
  let knownCaller: string | null = null;

  function advanceEpoch(previous: string | null, current: string | null): void {
    epoch += 1;
    knownCaller = current;
    deps.dispatch(CALLER_CHANGED_EVENT, { previousEmployeeId: previous, currentEmployeeId: current, epoch });
  }

  /**
   * The Caller switcher (or the pilot Reset) calls this the moment the persona changes, before any new answer arrives, so answers still
   * in flight for the previous Caller are dropped. Same Employee and no `force`: nothing changes. `force` is for Reset, where the data
   * changes under the same persona. Returns true when the epoch advanced.
   */
  function callerSwitched(employeeId: string | null, opts: { force?: boolean } = {}): boolean {
    if (opts.force !== true && employeeId !== null && employeeId === knownCaller) return false;
    advanceEpoch(knownCaller, employeeId);
    return true;
  }

  function observeCallerHeader(header: string | null): void {
    if (header === null || header === '') return;
    if (knownCaller === null) {
      knownCaller = header;
      return;
    }
    if (header !== knownCaller) advanceEpoch(knownCaller, header);
  }

  function stale(): WorkApiError {
    return new WorkApiError('stale', 'This answer belonged to a previous Caller and was dropped.');
  }

  async function request(path: string, spec: RequestSpec): Promise<unknown> {
    const scoped = spec.scoped !== false;
    const startEpoch = epoch;
    const controller = new AbortController();
    let timedOut = false;
    const limit = timeouts[spec.timeout];
    const handle = deps.setTimeoutFn(() => {
      timedOut = true;
      controller.abort();
    }, limit);
    try {
      let token = '';
      try {
        token = await deps.getToken();
      } catch {
        throw new WorkApiError('network', 'Could not get your sign-in token.');
      }
      const response = await deps.fetchImpl(`${deps.baseUrl()}${path}`, {
        method: spec.method ?? 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: spec.body === undefined ? undefined : JSON.stringify(spec.body),
        signal: controller.signal,
      });
      const raw = await response.text();
      const callerHeader = response.headers.get(CALLER_HEADER);
      if (scoped) {
        // An answer for a request that began under an older epoch is dropped WITHOUT reading its header: it is stale whatever it says,
        // and observing its (old) Caller would wrongly move the client back to the previous Caller.
        if (startEpoch !== epoch) throw stale();
        observeCallerHeader(callerHeader);
        if (startEpoch !== epoch) throw stale();
      }
      let parsed: unknown = undefined;
      let parsedOk = true;
      if (raw !== '') {
        try {
          parsed = JSON.parse(raw);
        } catch {
          parsedOk = false;
        }
      }
      if (!response.ok) {
        const body = isRecord(parsed) ? parsed : {};
        throw new WorkApiError(
          'http',
          typeof body.message === 'string' && body.message !== '' ? body.message : `The request failed (HTTP ${response.status}).`,
          {
            status: response.status,
            code: typeof body.error === 'string' ? body.error : null,
            details: isRecord(body.details) ? body.details : null,
            callerEmployeeId: callerHeader,
          }
        );
      }
      if (!parsedOk) throw invalid(path, response.status);
      return parsed;
    } catch (error) {
      if (error instanceof WorkApiError) throw error;
      // A failure that arrives after the Caller changed belongs to the previous Caller and is not shown either.
      if (scoped && startEpoch !== epoch) throw stale();
      if (timedOut) throw new WorkApiError('timeout', `No answer within ${Math.round(limit / 1000)} seconds.`);
      throw new WorkApiError('network', 'Could not reach the server.');
    } finally {
      deps.clearTimeoutFn(handle);
    }
  }

  function items(body: unknown, what: string): WorkTask[] {
    if (!isRecord(body) || !Array.isArray(body.items) || !body.items.every((i) => isRecord(i) && typeof i.id === 'string')) throw invalid(what, 200);
    return body.items as WorkTask[];
  }

  function itemResponse(body: unknown, what: string): WorkItemResponse {
    if (!isRecord(body) || !isRecord(body.item) || typeof body.item.id !== 'string') throw invalid(what, 200);
    return body as unknown as WorkItemResponse;
  }

  const get = (path: string, timeout: TimeoutClass) => request(path, { timeout });
  const post = (path: string, body: unknown, timeout: TimeoutClass = 'action') => request(path, { method: 'POST', body, timeout });

  const api = {
    // ── Caller epoch ──
    getCallerEpoch: (): number => epoch,
    getKnownCaller: (): string | null => knownCaller,
    callerSwitched,

    // ── reads ──
    async getMe(): Promise<WorkMe> {
      const body = await get('/work/me', 'action');
      if (!isRecord(body) || !isRecord(body.employee) || typeof body.employee.employeeId !== 'string') throw invalid('/work/me', 200);
      // A capability that is missing is treated as absent (fail closed): the UI offers less, never more.
      return {
        employee: body.employee as unknown as WorkEmployeeRef,
        canOversee: body.canOversee === true,
        companyOversight: body.companyOversight === true,
        canAssign: body.canAssign === true,
        canSearchJobs: body.canSearchJobs === true,
        canSearchCustomers: body.canSearchCustomers === true,
        canAddCustomerInPricing: body.canAddCustomerInPricing === true,
        pricingCustomerAccess: readPricingCustomerAccess(body.pricingCustomerAccess, body.canSearchCustomers === true),
        canAdministerGroups: body.canAdministerGroups === true,
        simulated: body.simulated === true,
        ...(isRecord(body.identity) ? { identity: body.identity as unknown as { simulated: boolean; label: string } } : {}),
      };
    },
    async listMine(status?: WorkStatusName): Promise<WorkList> {
      const query = status === undefined ? '' : `?status=${enc(status)}`;
      return { items: items(await get(`/work${query}`, 'list'), '/work') };
    },
    async listShared(): Promise<WorkList> {
      return { items: items(await get('/work/shared', 'list'), '/work/shared') };
    },
    async listOversight(): Promise<WorkOversightList> {
      const body = await get('/work/oversight', 'list');
      const list = items(body, '/work/oversight');
      const scope = isRecord(body) && (body.scope === 'company' || body.scope === 'manager') ? body.scope : undefined;
      return scope === undefined ? { items: list } : { items: list, scope };
    },
    /**
     * One page of the COMPLETED tasks in the Caller's oversight scope (`GET /work/oversight?status=completed`; same scope rule as the open list:
     * a manager's Direct_Reports, the company for the designated Owner). An older server ignores `status` and answers the open list with no
     * `filter`: that is refused (`FILTER_NOT_APPLIED`, see `isRouteUnavailable`), never shown as completed work.
     */
    async listOversightCompleted(query: PageQuery = {}): Promise<WorkOversightCompletedPage> {
      const body = await get(`/work/oversight?status=completed${pageParams(query)}`, 'list');
      const list = items(body, '/work/oversight');
      if (!isRecord(body) || body.filter !== 'completed') {
        throw new WorkApiError('invalid-response', 'This server cannot list completed tasks yet.', { status: 200, code: FILTER_NOT_APPLIED_CODE });
      }
      const scope = body.scope === 'company' || body.scope === 'manager' ? body.scope : undefined;
      const next = nextCursor(body, '/work/oversight');
      return { items: list, filter: 'completed', ...(scope !== undefined ? { scope } : {}), ...(next !== undefined ? { next } : {}) };
    },
    /** One page of the requests the Caller issued, newest first (`GET /work/issued`; the server resolves the Caller, there is no caller parameter). */
    async getIssued(query: PageQuery = {}): Promise<WorkIssuedPage> {
      const body = await get(`/work/issued${pageParams(query, '?')}`, 'list');
      if (!isRecord(body) || !Array.isArray(body.requests) || !body.requests.every(isIssuedEntry)) throw invalid('/work/issued', 200);
      const next = nextCursor(body, '/work/issued');
      return { requests: body.requests as WorkIssuedEntry[], ...(next !== undefined ? { next } : {}) };
    },
    /**
     * The Team Work list (`GET /work/team`). With the new routes enabled this is the oversight alias (the department is then a client-side
     * filter and the server ignores it); with them disabled it is the unchanged legacy route, which takes the department. Same URL shape as
     * the legacy `getTeamWorkItems`, but with the Caller epoch, the 30 s limit and the documented-shape check (Req 15.4, 15.10).
     */
    async listTeam(department?: string): Promise<WorkList> {
      const query = department ? `?department=${enc(department)}` : '';
      return { items: items(await get(`/work/team${query}`, 'list'), '/work/team') };
    },
    async getBell(): Promise<WorkBell> {
      const body = await get('/work/bell', 'count');
      if (!isRecord(body) || !isCount(body.openTaskCount) || !isCount(body.sharedQueueCount)) throw invalid('/work/bell', 200);
      return { openTaskCount: body.openTaskCount, sharedQueueCount: body.sharedQueueCount, entries: Array.isArray(body.entries) ? (body.entries as WorkBellEntry[]) : [] };
    },
    async getTask(id: string): Promise<WorkItemResponse> {
      return itemResponse(await get(`/work/${enc(id)}`, 'action'), '/work/{id}');
    },
    async getHistory(id: string): Promise<{ history: Record<string, unknown>[]; truncated: boolean }> {
      const body = await get(`/work/${enc(id)}/history`, 'action');
      if (!isRecord(body) || !Array.isArray(body.history)) throw invalid('/work/{id}/history', 200);
      return { history: body.history as Record<string, unknown>[], truncated: body.truncated === true };
    },
    async listEmployees(): Promise<WorkEmployeeList> {
      const body = await get('/work/employees', 'action');
      if (!isRecord(body) || !Array.isArray(body.employees)) throw invalid('/work/employees', 200);
      return { employees: body.employees as WorkEmployeeRef[] };
    },
    /**
     * Search the limited job directory (BUILD-046 J1). The server returns at most 20 active jobs and nothing for fewer than two characters. A
     * failure is a WorkApiError (503 STORE_UNAVAILABLE when the directory cannot be asked): the caller shows it with Retry, never as "no matches".
     */
    async searchJobs(q: string): Promise<WorkJobSearch> {
      const body = await get(`/work/jobs?q=${enc(q)}`, 'action');
      if (!isRecord(body) || !Array.isArray(body.jobs) || !body.jobs.every((j) => isRecord(j) && typeof j.jobId === 'string' && typeof j.label === 'string')) {
        throw invalid('/work/jobs', 200);
      }
      return { jobs: body.jobs as WorkJobPick[], more: body.more === true, minChars: typeof body.minChars === 'number' && body.minChars > 0 ? body.minChars : 2 };
    },
    /**
     * Search the limited customer directory (BUILD-046 customer association). The server returns at most 20 active customers and nothing for fewer
     * than two characters. A failure is a WorkApiError (503 STORE_UNAVAILABLE when the directory cannot be asked): the caller shows it with Retry,
     * never as "no matches". A separate route from the job search; nothing about jobs is read here.
     */
    async searchCustomers(q: string): Promise<WorkCustomerSearch> {
      const body = await get(`/work/customers?q=${enc(q)}`, 'action');
      if (!isRecord(body) || !Array.isArray(body.customers) || !body.customers.every((j) => isRecord(j) && typeof j.customerId === 'string' && typeof j.label === 'string')) {
        throw invalid('/work/customers', 200);
      }
      return { customers: body.customers as WorkCustomerPick[], more: body.more === true, minChars: typeof body.minChars === 'number' && body.minChars > 0 ? body.minChars : 2 };
    },
    /** One customer's current name and state (`GET /work/customers/{customerId}`), for a customer launch link. 404 / 503 arrive as a WorkApiError. */
    async getCustomer(customerId: string): Promise<WorkCustomerView> {
      const body = await get(`/work/customers/${enc(customerId)}`, 'action');
      const c = isRecord(body) ? body.customer : undefined;
      if (!isRecord(c) || typeof c.customerId !== 'string' || typeof c.label !== 'string') throw invalid('/work/customers/{customerId}', 200);
      return {
        customerId: c.customerId,
        label: c.label,
        ...(typeof c.place === 'string' && c.place !== '' ? { place: c.place } : {}),
        ...(c.state === 'active' || c.state === 'inactive' ? { state: c.state } : {}),
      };
    },

    // ── assign ──
    async previewAssign(request: AssignRequest): Promise<AssignPreview> {
      const body = await post('/work/assign/preview', request, 'count');
      if (!isRecord(body) || !isCount(body.eligibleCount) || typeof body.involvesGroup !== 'boolean') throw invalid('/work/assign/preview', 200);
      return {
        eligibleCount: body.eligibleCount,
        involvesGroup: body.involvesGroup,
        modeRequired: body.modeRequired === true,
        mode: (body.mode as AssignPreview['mode']) ?? null,
        recipients: Array.isArray(body.recipients) ? (body.recipients as AssignPreview['recipients']) : [],
        groups: Array.isArray(body.groups) ? (body.groups as AssignPreview['groups']) : [],
        excluded: Array.isArray(body.excluded) ? (body.excluded as AssignExcluded[]) : [],
        queueEmptyAtIssue: body.queueEmptyAtIssue === true,
      };
    },
    async assign(request: AssignRequest): Promise<AssignResponse> {
      const body = await post('/work/assign', request);
      if (!isRecord(body) || typeof body.requestId !== 'string' || !Array.isArray(body.items)) throw invalid('/work/assign', 200);
      return { ...(body as unknown as AssignResponse), excluded: Array.isArray(body.excluded) ? (body.excluded as AssignExcluded[]) : [] };
    },

    // ── task actions ──
    /** Take task. A lost claim is a WorkApiError with status 409, code ALREADY_CLAIMED and `details.owner`. */
    async claim(id: string): Promise<WorkItemResponse> {
      return itemResponse(await post(`/work/${enc(id)}/claim`, undefined), '/work/{id}/claim');
    },
    /**
     * Done. Without a note it sends NO body (Req 8.12). With a note (Done with a note) it sends `{ note: { text, activityDate? } }`. Bounded at
     * 10 s (Req 8.3). The answer carries `noteSaved` only when a note was sent; the caller decides saved or not saved from that flag alone.
     */
    async complete(id: string, note?: WorkNoteInput): Promise<WorkCompleteResponse> {
      const body = note === undefined ? undefined : { note: note.activityDate === undefined || note.activityDate === '' ? { text: note.text } : { text: note.text, activityDate: note.activityDate } };
      const answer = await post(`/work/${enc(id)}/complete`, body, 'done');
      const response = itemResponse(answer, '/work/{id}/complete') as WorkCompleteResponse;
      const raw = answer as Record<string, unknown>;
      const out: WorkCompleteResponse = { ...response };
      // Only a real boolean is a statement about the note; anything else is left out, so it can never read as "saved".
      if (typeof raw.noteSaved === 'boolean') out.noteSaved = raw.noteSaved;
      else delete out.noteSaved;
      if (typeof raw.noteReason === 'string') out.noteReason = raw.noteReason;
      else delete out.noteReason;
      if (raw.noteReplayed === true) out.noteReplayed = true;
      else delete out.noteReplayed;
      if (isRecord(raw.note)) out.note = raw.note as unknown as WorkCompletionNote;
      else delete out.note;
      return out;
    },
    /**
     * The Update form (BUILD-046 section C): ONE request saves the note and the field changes together. A refusal is a WorkApiError (code and details.field say what to fix);
     * nothing is saved then, and the caller keeps the draft. Only a real `noteSaved` boolean counts as a statement about the note.
     */
    async progress(id: string, input: WorkProgressInput): Promise<WorkProgressResponse> {
      const answer = await post(`/work/${enc(id)}/progress`, input, 'action');
      const response = itemResponse(answer, '/work/{id}/progress');
      const raw = answer as Record<string, unknown>;
      const out: WorkProgressResponse = { ...response, noteSaved: raw.noteSaved === true, changed: Array.isArray(raw.changed) ? raw.changed.filter((c): c is string => typeof c === 'string') : [] };
      if (isRecord(raw.note)) out.note = raw.note as unknown as WorkProgressNote;
      return out;
    },
    /** Undo after Done (the existing restore route), bounded at 10 s. */
    async restore(id: string): Promise<WorkItemResponse> {
      return itemResponse(await post(`/work/${enc(id)}/restore`, undefined, 'done'), '/work/{id}/restore');
    },
    async assignTo(id: string, input: { targetEmployeeId: string; expectedOwnerEmployeeId?: string | null }): Promise<WorkItemResponse> {
      return itemResponse(await post(`/work/${enc(id)}/assign-to`, input), '/work/{id}/assign-to');
    },
    async reassign(id: string, input: { targetEmployeeId: string; expectedOwnerEmployeeId?: string | null }): Promise<WorkItemResponse> {
      return itemResponse(await post(`/work/${enc(id)}/reassign`, input), '/work/{id}/reassign');
    },
    async returnToQueue(id: string, input: { expectedOwnerEmployeeId?: string | null } = {}): Promise<WorkItemResponse> {
      return itemResponse(await post(`/work/${enc(id)}/return-to-queue`, input), '/work/{id}/return-to-queue');
    },
    async cancelTask(id: string): Promise<WorkItemResponse> {
      return itemResponse(await post(`/work/${enc(id)}/cancel`, undefined), '/work/{id}/cancel');
    },

    // ── Everyone sets ──
    async getSet(requestId: string): Promise<Record<string, unknown>> {
      const body = await get(`/work/sets/${enc(requestId)}`, 'action');
      if (!isRecord(body) || !isRecord(body.set)) throw invalid('/work/sets/{id}', 200);
      return body;
    },
    async addSetRecipient(requestId: string, employeeId: string): Promise<Record<string, unknown>> {
      return (await post(`/work/sets/${enc(requestId)}/recipients`, { employeeId })) as Record<string, unknown>;
    },
    async removeSetRecipient(requestId: string, employeeId: string): Promise<Record<string, unknown>> {
      return (await request(`/work/sets/${enc(requestId)}/recipients/${enc(employeeId)}`, { method: 'DELETE', timeout: 'action' })) as Record<string, unknown>;
    },
    async cancelSet(requestId: string): Promise<Record<string, unknown>> {
      return (await post(`/work/sets/${enc(requestId)}/cancel`, undefined)) as Record<string, unknown>;
    },

    // ── Work groups ──
    async listGroups(): Promise<{ groups: WorkGroupRef[] }> {
      const body = await get('/work/groups', 'list');
      if (!isRecord(body) || !Array.isArray(body.groups)) throw invalid('/work/groups', 200);
      return { groups: body.groups as WorkGroupRef[] };
    },
    async groupMembers(groupId: string): Promise<{ group: WorkGroupRef; members: WorkGroupMember[] }> {
      const body = await get(`/work/groups/${enc(groupId)}/members`, 'list');
      if (!isRecord(body) || !isRecord(body.group) || !Array.isArray(body.members)) throw invalid('/work/groups/{id}/members', 200);
      return { group: body.group as unknown as WorkGroupRef, members: body.members as WorkGroupMember[] };
    },
    async createGroup(name: string): Promise<{ group: WorkGroupRef }> {
      const body = await post('/work/groups', { name });
      if (!isRecord(body) || !isRecord(body.group)) throw invalid('/work/groups', 201);
      return { group: body.group as unknown as WorkGroupRef };
    },
    async addGroupMembers(groupId: string, employeeIds: string[]): Promise<Record<string, unknown>> {
      return (await post(`/work/groups/${enc(groupId)}/members`, { employeeIds })) as Record<string, unknown>;
    },
    async removeGroupMembers(groupId: string, employeeIds: string[]): Promise<Record<string, unknown>> {
      return (await post(`/work/groups/${enc(groupId)}/members/remove`, { employeeIds })) as Record<string, unknown>;
    },

    // ── pilot only (`/local/*`): not Caller-scoped, never dropped as stale ──
    async getPersonas(): Promise<PilotCallerList> {
      const body = await request('/local/switch-caller', { timeout: 'action', scoped: false });
      if (!isRecord(body) || !Array.isArray(body.people)) throw invalid('/local/switch-caller', 200);
      return body as unknown as PilotCallerList;
    },
    /** Switches the server-held persona, then tells every list to reload (Req 2.7). A refused switch (400) throws and changes nothing here. */
    async switchCaller(employeeId: string): Promise<PilotCallerList> {
      const body = await request('/local/switch-caller', { method: 'POST', body: { employeeId }, timeout: 'action', scoped: false });
      if (!isRecord(body) || !Array.isArray(body.people)) throw invalid('/local/switch-caller', 200);
      const result = body as unknown as PilotCallerList;
      callerSwitched(typeof result.currentCallerEmployeeId === 'string' ? result.currentCallerEmployeeId : null);
      return result;
    },
    /** `POST /local/reset`: archives and re-seeds the pilot data and puts the persona back to the default, so every list reloads. */
    async resetPilot(): Promise<PilotResetResult> {
      const body = await request('/local/reset', { method: 'POST', timeout: 'action', scoped: false });
      if (!isRecord(body) || body.ok !== true) throw invalid('/local/reset', 200);
      callerSwitched(null, { force: true });
      return body as unknown as PilotResetResult;
    },
  };
  return api;
}

export type WorkClient = ReturnType<typeof createWorkClient>;

/** The app-wide client. Views import this; tests build their own with `createWorkClient`. */
export const workApi: WorkClient = createWorkClient();

/** Tells the bell and the counts to refresh after a change the Caller made (same tab; design "Tasks bell"). */
export function notifySharedTasksChanged(detail: Record<string, unknown> = {}): void {
  defaultDispatch(TASKS_CHANGED_EVENT, detail);
}

/** Subscribes to `caller-changed`; returns the unsubscribe function. A no-op outside a browser. */
export function onCallerChanged(handler: (detail: { previousEmployeeId: string | null; currentEmployeeId: string | null; epoch: number }) => void): () => void {
  const w = (globalThis as any).window;
  if (!w || typeof w.addEventListener !== 'function') return () => undefined;
  const listener = (event: Event) => handler((event as CustomEvent).detail);
  w.addEventListener(CALLER_CHANGED_EVENT, listener);
  return () => w.removeEventListener(CALLER_CHANGED_EVENT, listener);
}
